import { createStore } from 'vuex'
import api from '@/services/api'
import { getSubcategoryDisplayName, getCategoryDisplayName } from '@/utils/categoryConstants'

// Helper function to handle API errors
const handleApiError = (commit, error, errorType) => {
  console.error(errorType, error)
  let errorMessage = 'An error occurred. Please try again.'
  let detailedError = null;
  
  // Extract error details from the response if available
  if (error.response && error.response.data) {
    // Handle string error
    if (typeof error.response.data === 'string') {
      errorMessage = error.response.data;
    } 
    // Handle object errors with various properties
    else if (typeof error.response.data === 'object') {
      detailedError = error.response.data;
      // Try to extract the most relevant error message
      if (error.response.data.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.data.non_field_errors) {
        // Common Django REST framework error format
        errorMessage = error.response.data.non_field_errors.join(', ');
      }
    }
  } else if (error.message) {
    errorMessage = error.message;
  }
  
  // Create a well-structured error object
  const errorObj = { 
    type: errorType, 
    message: errorMessage,
    status: error.response ? error.response.status : null,
    data: detailedError
  };
  
  commit('setError', errorObj);
  return Promise.reject(error);
}

export default createStore({
  state: {
    // Auth
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    authToken: localStorage.getItem('authToken') || '',
    
    // Courses - Initialize with defensive getter
    courses: [],
    currentCourse: null,
    
    // Videos
    videos: [],
    currentVideo: null,
    
    // Homepage data
    sliderImages: [],
    featuredCourses: [],
    testimonials: [],
    
    // UI state
    loading: false,
    error: null
  },
  
  getters: {
    // Auth
    isAuthenticated: state => !!state.authToken,
    isAdmin: state => state.user && state.user.is_staff,
    userCategory: state => {
      if (!state.user) return null;
      
      // Priority 1: Use subcategory if available for more specific categorization
      if (state.user.subcategory) {
        return getSubcategoryDisplayName(state.user.subcategory);
      }
      
      // Priority 2: Use category_name if available (from backend)
      if (state.user.category_name) {
        return state.user.category_name;
      }
      
      // Priority 3: Map category to display name
      if (state.user.category) {
        return getCategoryDisplayName(state.user.category);
      }
      
      // Fallback to raw category value
      return state.user.category;
    },
    userCategoryRaw: state => state.user ? state.user.category : null,
    userSubcategory: state => state.user ? state.user.subcategory : null,
    
    // Courses
    courseById: state => id => state.courses.find(course => course.id === id),
    coursesByCategory: state => category => {
      if (!category) return state.courses;
      return state.courses.filter(course => course.category.toLowerCase() === category.toLowerCase());
    },
    coursesForCurrentUser: (state, getters) => {
      if (!state.user) return [];
      const userCategory = getters.userCategory;
      
      // If user is admin, return all courses (including drafts and under review)
      if (getters.isAdmin) return state.courses;
      
      // For regular users, apply multiple filters
      return state.courses.filter(course => {
        // 1. First filter: Only show published courses to regular users
        if (course.status && course.status.toLowerCase() !== 'published') {
          return false;
        }
        
        // 2. Filter by target audience - check all possible audience fields
        
        // Check target_audience array field
        if (course.target_audience) {
          let targetAudience = [];
          
          // Parse if it's a JSON string
          if (typeof course.target_audience === 'string' && course.target_audience.trim().startsWith('[')) {
            try {
              targetAudience = JSON.parse(course.target_audience);
            } catch (e) {
              console.warn('Error parsing target_audience:', e);
            }
          } 
          // Use directly if it's already an array
          else if (Array.isArray(course.target_audience)) {
            targetAudience = course.target_audience;
          }
          
          // If target audience is specified and user category isn't in it, hide course
          if (targetAudience.length > 0 && !targetAudience.includes(userCategory)) {
            return false;
          }
        }
        
        // Also check user_category_ids field
        if (course.user_category_ids) {
          let categoryIds = [];
          
          // Parse if it's a JSON string
          if (typeof course.user_category_ids === 'string' && course.user_category_ids.trim().startsWith('[')) {
            try {
              categoryIds = JSON.parse(course.user_category_ids);
            } catch (e) {
              console.warn('Error parsing user_category_ids:', e);
            }
          }
          // Use directly if it's already an array
          else if (Array.isArray(course.user_category_ids)) {
            categoryIds = course.user_category_ids;
          }
          
          // Convert user category string to ID for comparison
          const categoryStringToId = {
            'nurse': 1,
            'physician-assistant': 2,
            'medical-student': 3,
            'doctor': 4,
            'foreign-exam': 5
          };
          
          const userCategoryId = isNaN(parseInt(userCategory)) 
            ? categoryStringToId[userCategory] 
            : parseInt(userCategory);
          
          // If category IDs are specified and user's category ID isn't in it, hide course
          if (categoryIds.length > 0 && !categoryIds.includes(userCategoryId)) {
            return false;
          }
        }
        
        // Also check backward compatibility field 'targetAudience'
        if (course.targetAudience && Array.isArray(course.targetAudience) && 
            course.targetAudience.length > 0 && !course.targetAudience.includes(userCategory)) {
          return false;
        }
        
        // If we've passed all filters, show the course
        return true;
      });
    },
    
    // Videos
    videoById: state => id => state.videos.find(video => video.id === id),
    videosByCategory: state => category => {
      if (!category) return state.videos;
      return state.videos.filter(video => video.category === category);
    },
    
    // Homepage data
    sliderImages: state => state.sliderImages,
    featuredCourses: state => state.featuredCourses,
    testimonials: state => state.testimonials
  },
  
  mutations: {
    // Auth mutations
    setUser(state, user) {
      state.user = user;
      localStorage.setItem('user', JSON.stringify(user));
    },
    setAuthToken(state, token) {
      state.authToken = token;
      localStorage.setItem('authToken', token);
    },
    clearAuth(state) {
      state.user = null;
      state.authToken = '';
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
    },
    
    // Course mutations
    setCourses(state, courses) {
      // Ensure courses is always an array
      if (!Array.isArray(courses)) {
        console.error('setCourses called with non-array value:', typeof courses, courses);
        courses = [];
      }
      
      console.log('setCourses: Setting courses array with', courses.length, 'items');
      
      // Update module counts for all courses
      courses = courses.map(course => {
        // Create a copy to avoid mutation
        const updatedCourse = { ...course };
        
        // If course has modules array, update the modulesCount
        if (updatedCourse.modules && Array.isArray(updatedCourse.modules)) {
          updatedCourse.modulesCount = updatedCourse.modules.length;
          
          // Also update lessons count
          let totalLessons = 0;
          updatedCourse.modules.forEach(module => {
            if (module.lessons && Array.isArray(module.lessons)) {
              totalLessons += module.lessons.length;
            }
          });
          updatedCourse.lessonsCount = totalLessons;
        }
        
        return updatedCourse;
      });
      
      state.courses = courses;
    },
    setCurrentCourse(state, course) {
      state.currentCourse = course;
    },
    addCourse(state, course) {
      console.log('addCourse: Current state.courses type:', typeof state.courses, Array.isArray(state.courses));
      
      // Ensure courses is an array before adding
      if (!Array.isArray(state.courses)) {
        console.error('State.courses is not an array:', state.courses);
        state.courses = [];
      }
      
      console.log('addCourse: Adding course with ID:', course.id);
      
      // Use spread operator instead of unshift to avoid potential issues
      state.courses = [course, ...state.courses];
      
      console.log('addCourse: Courses array now has', state.courses.length, 'items');
    },
    updateCourse(state, updatedCourse) {
      const index = state.courses.findIndex(course => course.id === updatedCourse.id);
      if (index !== -1) {
        // Update module count if not already set
        const courseToUpdate = { ...updatedCourse };
        if (courseToUpdate.modules && Array.isArray(courseToUpdate.modules)) {
          courseToUpdate.modulesCount = courseToUpdate.modules.length;
          
          // Also update lessons count if needed
          let totalLessons = 0;
          courseToUpdate.modules.forEach(module => {
            if (module.lessons && Array.isArray(module.lessons)) {
              totalLessons += module.lessons.length;
            }
          });
          courseToUpdate.lessonsCount = totalLessons;
        }
        
        // Create a new array with the updated course
        state.courses = [
          ...state.courses.slice(0, index),
          courseToUpdate,
          ...state.courses.slice(index + 1)
        ];
      }
    },
    deleteCourse(state, courseId) {
      state.courses = state.courses.filter(course => course.id !== courseId);
    },
    
    // Homepage data mutations
    setHomepageData(state, data) {
      state.sliderImages = data.slider_images || [];
      state.featuredCourses = data.featured_courses || [];
      state.testimonials = data.testimonials || [];
    },
    
    // Video mutations
    setVideos(state, videos) {
      state.videos = videos;
    },
    setCurrentVideo(state, video) {
      state.currentVideo = video;
    },
    addVideo(state, video) {
      // Ensure videos is an array before adding
      if (!Array.isArray(state.videos)) {
        console.warn('State.videos is not an array, initializing as empty array');
        state.videos = [];
      }
      // Use spread operator instead of unshift to avoid potential issues
      state.videos = [video, ...state.videos];
    },
    updateVideo(state, updatedVideo) {
      const index = state.videos.findIndex(video => video.id === updatedVideo.id);
      if (index !== -1) {
        // Create a new array with the updated video
        state.videos = [
          ...state.videos.slice(0, index),
          updatedVideo,
          ...state.videos.slice(index + 1)
        ];
      }
    },
    deleteVideo(state, videoId) {
      state.videos = state.videos.filter(video => video.id !== videoId);
    },
    
    // UI mutations
    setLoading(state, isLoading) {
      state.loading = isLoading;
    },
    setError(state, error) {
      // Ensure error is properly structured with a message property
      if (typeof error === 'string') {
        state.error = { message: error };
      } else if (!error || typeof error !== 'object') {
        state.error = { message: 'An unknown error occurred' };
      } else if (!error.message) {
        // If error is an object but doesn't have a message property
        state.error = { 
          ...error, 
          message: error.type || 'An error occurred. Please try again.' 
        };
      } else {
        state.error = error;
      }
    },
    clearError(state) {
      state.error = null;
    }
  },
  
  actions: {
    // Auth actions
    async login({ commit }, credentials) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        const response = await api.login(credentials);
        commit('setAuthToken', response.data.token);
        commit('setUser', response.data.user);
        commit('setLoading', false);
        return response;
      } catch (error) {
        commit('setLoading', false);
        return handleApiError(commit, error, 'Login Error');
      }
    },
    
    async refreshProfile({ commit }) {
      try {
        const response = await api.refreshProfile();
        commit('setUser', response.data);
        return response;
      } catch (error) {
        return handleApiError(commit, error, 'Profile Refresh Error');
      }
    },
    
    async register({ commit }, userData) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        const response = await api.register(userData);
        commit('setLoading', false);
        return response;
      } catch (error) {
        commit('setLoading', false);
        return handleApiError(commit, error, 'Registration Error');
      }
    },
    
    async verifyPayment({ commit }, reference) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        const response = await api.verifyPayment(reference);
        commit('setLoading', false);
        return response;
      } catch (error) {
        commit('setLoading', false);
        return handleApiError(commit, error, 'Payment Verification Error');
      }
    },
    
    async verifyDiscountCode({ commit }, code) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        const response = await api.verifyDiscount(code);
        commit('setLoading', false);
        return response;
      } catch (error) {
        commit('setLoading', false);
        return handleApiError(commit, error, 'Discount Code Verification Error');
      }
    },
    
    async fetchPricingPlanByCategory({ commit }, category) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        const response = await api.getPricingPlanByCategory(category);
        commit('setLoading', false);
        return response;
      } catch (error) {
        commit('setLoading', false);
        return handleApiError(commit, error, 'Failed to fetch pricing plan');
      }
    },
    
    async fetchPricingPlanBySubcategory({ commit }, subcategory) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        const response = await api.getPricingPlanBySubcategory(subcategory);
        commit('setLoading', false);
        return response;
      } catch (error) {
        commit('setLoading', false);
        return handleApiError(commit, error, 'Failed to fetch pricing plan');
      }
    },
    
    async logout({ commit }) {
      commit('setLoading', true);
      
      try {
        // Attempt to call the backend logout endpoint
        await api.logout();
        console.log('Backend logout successful');
      } catch (error) {
        console.warn('Backend logout failed, proceeding with local logout:', error.message);
      } finally {
        // Always clear authentication state in both store and localStorage
        commit('clearAuth');
        commit('setLoading', false);
        console.log('Authentication state cleared');
      }
    },
    
    // Course actions
    async fetchCourses({ commit }) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        console.log('[Store] Fetching courses from API...');
        // Include detailed data (modules and lessons) for admin users
        const response = await api.getCourses({ include_details: 'true' });
        console.log('[Store] API response:', response);
        console.log('[Store] Raw response data:', response.data);
        
        // Handle paginated response - check if response.data is an object with results
        let coursesData = response.data;
        if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
          // Check if it's a paginated response with 'results' field
          if (response.data.results && Array.isArray(response.data.results)) {
            coursesData = response.data.results;
            console.log('[Store] Using paginated results:', coursesData);
          } else {
            console.error('[Store] Unexpected response format - not an array or paginated response:', response.data);
            coursesData = [];
          }
        }
        
        console.log('[Store] Final courses data:', coursesData);
        
        // Log details about handwritten notes
        if (Array.isArray(coursesData)) {
          coursesData.forEach((course, index) => {
            console.log(`[Store] Course ${index + 1} (ID: ${course.id}, Title: ${course.title}):`);
            console.log(`  - Modules:`, course.modules ? course.modules.length : 'none');
            
            // Check lessons within modules
            if (course.modules && Array.isArray(course.modules)) {
              course.modules.forEach((module, modIndex) => {
                console.log(`    Module ${modIndex + 1} (${module.title}):`);
                console.log(`      - Lessons:`, module.lessons ? module.lessons.length : 'none');
                
                if (module.lessons && Array.isArray(module.lessons)) {
                  module.lessons.forEach((lesson, lesIndex) => {
                    console.log(`        Lesson ${lesIndex + 1} (${lesson.title}):`);
                    console.log(`          - handwritten_note_images:`, lesson.handwritten_note_images);
                    console.log(`          - handwritten_notes:`, lesson.handwritten_notes);
                  });
                }
              });
            }
          });
        }
        
        commit('setCourses', coursesData);
        commit('setLoading', false);
        return coursesData;
      } catch (error) {
        commit('setLoading', false);
        console.error('[Store] Error fetching courses:', error);
        return handleApiError(commit, error, 'Failed to fetch courses');
      }
    },
    
    async fetchCourse({ commit }, courseId) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        console.log(`[Store] Fetching course ${courseId} from API...`);
        const response = await api.getCourse(courseId);
        console.log('[Store] Single course API response:', response);
        console.log('[Store] Single course data:', response.data);
        
        // Log details about handwritten notes
        if (response.data) {
          console.log(`[Store] Course details (ID: ${response.data.id}, Title: ${response.data.title}):`);
          console.log('  - handwritten_note_images:', response.data.handwritten_note_images);
          console.log('  - handwritten_notes:', response.data.handwritten_notes);
          console.log('  - All keys:', Object.keys(response.data));
        }
        
        commit('setCurrentCourse', response.data);
        commit('setLoading', false);
        return response.data;
      } catch (error) {
        commit('setLoading', false);
        console.error(`[Store] Error fetching course ${courseId}:`, error);
        return handleApiError(commit, error, 'Failed to fetch course');
      }
    },
    
    async createCourse({ commit, state }, courseData) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        // Ensure courses array exists before creating
        if (!Array.isArray(state.courses)) {
          console.warn('Courses not loaded, initializing empty array');
          commit('setCourses', []);
        }
        
        const response = await api.createCourse(courseData);
        
        // Instead of using addCourse mutation, use setCourses with new array
        // This completely avoids any unshift-related issues
        const currentCourses = Array.isArray(state.courses) ? [...state.courses] : [];
        const newCourses = [response.data, ...currentCourses];
        commit('setCourses', newCourses);
        
        commit('setLoading', false);
        return response.data;
      } catch (error) {
        commit('setLoading', false);
        return handleApiError(commit, error, 'Failed to create course');
      }
    },
    
    async updateCourse({ commit }, { id, courseData }) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        console.log('updateCourse action - ID:', id, 'Data:', courseData);
        
        // Ensure we have the required fields
        if (!courseData.title) {
          throw new Error('Course title is required');
        }
        if (!courseData.description) {
          throw new Error('Course description is required');
        }
        
        // Make the API call
        const response = await api.updateCourse(id, courseData);
        console.log('updateCourse response:', response.data);
        
        // Debug PDF data in response
        if (response.data && response.data.modules) {
          const totalPDFs = response.data.modules.reduce((total, module) => {
            return total + (module.lessons?.reduce((lessonTotal, lesson) => {
              return lessonTotal + (lesson.pdf_files?.length || 0);
            }, 0) || 0);
          }, 0);
          console.log(`DEBUG: updateCourse response contains ${totalPDFs} PDFs across all lessons`);
          
          response.data.modules.forEach((module, modIndex) => {
            module.lessons?.forEach((lesson, lessonIndex) => {
              if (lesson.pdf_files && lesson.pdf_files.length > 0) {
                console.log(`DEBUG: Response lesson "${lesson.title}" has ${lesson.pdf_files.length} PDFs:`, lesson.pdf_files.map(p => p.title || p.id));
              }
            });
          });
        }
        commit('updateCourse', response.data);
        commit('setLoading', false);
        return response.data;
      } catch (error) {
        commit('setLoading', false);
        return handleApiError(commit, error, 'Failed to update course');
      }
    },
    
    async deleteCourse({ commit }, courseId) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        await api.deleteCourse(courseId);
        commit('deleteCourse', courseId);
        commit('setLoading', false);
        return true;
      } catch (error) {
        commit('setLoading', false);
        return handleApiError(commit, error, 'Failed to delete course');
      }
    },
    
    // Video actions
    async fetchVideos({ commit }) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        const response = await api.getVideos();
        commit('setVideos', response.data);
        commit('setLoading', false);
        return response.data;
      } catch (error) {
        commit('setLoading', false);
        return handleApiError(commit, error, 'Failed to fetch videos');
      }
    },
    
    async fetchVideo({ commit }, videoId) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        const response = await api.getVideo(videoId);
        commit('setCurrentVideo', response.data);
        commit('setLoading', false);
        return response.data;
      } catch (error) {
        commit('setLoading', false);
        return handleApiError(commit, error, 'Failed to fetch video');
      }
    },
    
    async createVideo({ commit }, videoData) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        const response = await api.createVideo(videoData);
        commit('addVideo', response.data);
        commit('setLoading', false);
        return response.data;
      } catch (error) {
        commit('setLoading', false);
        return handleApiError(commit, error, 'Failed to create video');
      }
    },
    
    async updateVideo({ commit }, { id, videoData }) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        const response = await api.updateVideo(id, videoData);
        commit('updateVideo', response.data);
        commit('setLoading', false);
        return response.data;
      } catch (error) {
        commit('setLoading', false);
        return handleApiError(commit, error, 'Failed to update video');
      }
    },
    
    async deleteVideo({ commit }, videoId) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        await api.deleteVideo(videoId);
        commit('deleteVideo', videoId);
        commit('setLoading', false);
        return true;
      } catch (error) {
        commit('setLoading', false);
        return handleApiError(commit, error, 'Failed to delete video');
      }
    },
    
    async uploadVideo({ commit }, { formData, onProgress }) {
      commit('setLoading', true);
      commit('clearError');
      
      try {
        const response = await api.uploadVideo(formData, onProgress);
        commit('setLoading', false);
        return response.data;
      } catch (error) {
        commit('setLoading', false);
        return handleApiError(commit, error, 'Failed to upload video');
      }
    },
    
    // Homepage data actions
    async fetchHomepageData({ commit }) {
      commit('setLoading', true);
      commit('clearError');
      
      // Helper to get proper media URL based on environment - UPDATED to use public directory
      const getMediaUrl = (path) => {
        // Use public directory images for all environments
        // Add timestamp to force cache refresh
        const timestamp = Date.now();
        console.log(`Loading slider image: /images/slider/${path}?t=${timestamp}`);
        return `/images/slider/${path}?t=${timestamp}`;
      };
      
      try {
        // Try to fetch slider images only from backend
        let sliderImages = [];
        
        try {
          const response = await api.getHomepageData();
          // Only use slider images from the API
          sliderImages = response.data.slider_images || [];
          commit('setLoading', false);
        } catch (apiError) {
          console.warn('Backend API not available, using default slider images');
          
          // Default slider images
          sliderImages = [
            {
              id: 1,
              title: "Welcome to TutorMed",
              description: "Simplifying complex medical concepts for health personnel worldwide",
              image_url: getMediaUrl('slide1.jpg')
            },
            {
              id: 2,
              title: "Specialized Programs",
              description: "Tailored content for medical students, doctors, nurses, and more",
              image_url: getMediaUrl('slide2.jpg')
            },
            {
              id: 3,
              title: "Join Our Global Community",
              description: "Connect with healthcare professionals around the world",
              image_url: getMediaUrl('slide3.jpg')
            },
            {
              id: 4,
              title: "Expert Medical Training",
              description: "Learn from experienced healthcare professionals",
              image_url: getMediaUrl('slide4.jpg')
            },
            {
              id: 5,
              title: "Comprehensive Resources",
              description: "Access a wide range of medical educational materials",
              image_url: getMediaUrl('slide5.jpg')
            },
            {
              id: 6,
              title: "Advanced Medical Education",
              description: "Cutting-edge learning techniques for modern healthcare",
              image_url: getMediaUrl('slide6.jpg')
            }
          ];
        }
        
        // ALWAYS use hardcoded featured courses and testimonials as requested
        const featuredCourses = [
          {
            id: 1,
            title: "Medical Students Program",
            description: "Comprehensive preparation for MCQ and OSCE examinations.",
            thumbnail: require('@/assets/img/programs/student.png'),
            link: "/programs/medical-students"
          },
          {
            id: 2,
            title: "House Officers Training",
            description: "Clinical management and diagnostic skills for new doctors.",
            thumbnail: require('@/assets/img/programs/doctor.png'),
            link: "/programs/doctors"
          },
          {
            id: 3,
            title: "Nursing Skills Development",
            description: "Essential modules for nursing professionals.",
            thumbnail: require('@/assets/img/programs/nurses.png'),
            link: "/programs/nurses"
          },
          {
            id: 4,
            title: "Physician Assistant Program",
            description: "Structured learning in core medical subjects with pre-scheduled lectures.",
            thumbnail: require('@/assets/img/programs/pa.png'),
            link: "/programs/physician-assistants"
          },
          {
            id: 5,
            title: "GMDC Preparation",
            description: "Specialized training with MCQ recalls, OSCE practice, and targeted materials.",
            thumbnail: require('@/assets/img/programs/exam.png'),
            link: "/programs/gmdc"
          },
          {
            id: 6,
            title: "Foreign Exams (USMLE, PLAB)",
            description: "Preparatory courses for international medical licensing examinations.",
            thumbnail: require('@/assets/img/programs/usmle.png'),
            link: "/programs/foreign-exams"
          }
        ];
        
        const testimonials = [
          {
            id: 1,
            name: "Dr(Med). Deborah Delali Hanyabui",
            role: "House Officer, Tema General Hospital",
            text: "TutorMed has been a game-changer for my medical education. The OSCE preparation resources helped me refine my clinical examination skills and approach patients with confidence. The tutors are incredibly knowledgeable and supportive.",
            image: require('@/assets/img/testimonials/deborah.jpg')
          },
          {
            id: 2,
            name: "Dr(Med). Matthew Tamakloe",
            role: "House Officer, Tamale Teaching Hospital",
            text: "As a newly qualified doctor, TutorMed's clinical management modules were exactly what I needed to navigate my house officer rotations. The practical focus helped bridge the gap between theory and real-world practice.",
            image: require('@/assets/img/testimonials/matthew.jpg')
          },
          {
            id: 3,
            name: "Sandra Esi Eshun",
            role: "House Officer, Sunyani Regional Hospital",
            text: "The specialized content for medical students at TutorMed covered all essential topics in a clear, understandable way. It allowed me to study at my own pace while balancing clinical rotations.",
            image: require('@/assets/img/testimonials/sandra.jpg')
          },
          {
            id: 4,
            name: "Francis Agyei",
            role: "Physician Assistant",
            text: "TutorMed's pre-scheduled lectures were perfectly tailored to my needs as a physician assistant. The comprehensive coverage of core subjects has significantly improved my diagnostic abilities and patient care.",
            image: require('@/assets/img/testimonials/pa-icon.png')
          }
        ];
        
        // Commit the combined data
        commit('setHomepageData', {
          slider_images: sliderImages,
          featured_courses: featuredCourses,
          testimonials: testimonials
        });
        
        commit('setLoading', false);
        
        // Return the combined data
        return {
          slider_images: sliderImages,
          featured_courses: featuredCourses,
          testimonials: testimonials
        };
      } catch (error) {
        console.error('Error in fetchHomepageData:', error);
        
        // Fallback to completely default data if something went wrong
        const fallbackData = {
          slider_images: [
            {
              id: 1,
              title: "Welcome to TutorMed",
              description: "Simplifying complex medical concepts for health personnel worldwide",
              image_url: getMediaUrl('slide1.jpg')
            },
            {
              id: 2,
              title: "Specialized Programs",
              description: "Tailored content for medical students, doctors, nurses, and more",
              image_url: getMediaUrl('slide2.jpg')
            },
            {
              id: 3,
              title: "Join Our Global Community",
              description: "Connect with healthcare professionals around the world",
              image_url: getMediaUrl('slide3.jpg')
            },
            {
              id: 4,
              title: "Expert Medical Training",
              description: "Learn from experienced healthcare professionals",
              image_url: getMediaUrl('slide4.jpg')
            },
            {
              id: 5,
              title: "Comprehensive Resources",
              description: "Access a wide range of medical educational materials",
              image_url: getMediaUrl('slide5.jpg')
            },
            {
              id: 6,
              title: "Advanced Medical Education",
              description: "Cutting-edge learning techniques for modern healthcare",
              image_url: getMediaUrl('slide6.jpg')
            }
          ],
          featured_courses: [
            {
              id: 1,
              title: "Medical Students Program",
              description: "Comprehensive preparation for MCQ and OSCE examinations.",
              thumbnail: require('@/assets/img/programs/student.png'),
              link: "/programs/medical-students"
            },
            {
              id: 2,
              title: "House Officers Training",
              description: "Clinical management and diagnostic skills for new doctors.",
              thumbnail: require('@/assets/img/programs/doctor.png'),
              link: "/programs/doctors"
            },
            {
              id: 3,
              title: "Nursing Skills Development",
              description: "Essential modules for nursing professionals.",
              thumbnail: require('@/assets/img/programs/nurses.png'),
              link: "/programs/nurses"
            },
            {
              id: 4,
              title: "Physician Assistant Program",
              description: "Structured learning in core medical subjects with pre-scheduled lectures.",
              thumbnail: require('@/assets/img/programs/pa.png'),
              link: "/programs/physician-assistants"
            },
            {
              id: 5,
              title: "GMDC Preparation",
              description: "Specialized training with MCQ recalls, OSCE practice, and targeted materials.",
              thumbnail: require('@/assets/img/programs/exam.png'),
              link: "/programs/gmdc"
            },
            {
              id: 6,
              title: "Foreign Exams (USMLE, PLAB)",
              description: "Preparatory courses for international medical licensing examinations.",
              thumbnail: require('@/assets/img/programs/usmle.png'),
              link: "/programs/foreign-exams"
            }
          ],
          testimonials: [
            {
              id: 1,
              name: "Dr(Med). Deborah Delali Hanyabui",
              role: "House Officer, Tema General Hospital",
              text: "TutorMed has been a game-changer for my medical education. The OSCE preparation resources helped me refine my clinical examination skills and approach patients with confidence. The tutors are incredibly knowledgeable and supportive.",
              image: require('@/assets/img/testimonials/deborah.jpg')
            },
            {
              id: 2,
              name: "Dr(Med). Matthew Tamakloe", 
              role: "House Officer, Tamale Teaching Hospital",
              text: "As a newly qualified doctor, TutorMed's clinical management modules were exactly what I needed to navigate my house officer rotations. The practical focus helped bridge the gap between theory and real-world practice.",
              image: require('@/assets/img/testimonials/matthew.jpg')
            },
            {
              id: 3,
              name: "Dr(Med). Sandra Esi Eshun",
              role: "House Officer, Sunyani Regional Hospital",
              text: "The specialized content for medical students at TutorMed covered all essential topics in a clear, understandable way. It allowed me to study at my own pace while balancing clinical rotations.",
              image: require('@/assets/img/testimonials/sandra.jpg')
            },
            {
              id: 4,
              name: "Francis Agyei",
              role: "Physician Assistant",
              text: "TutorMed's pre-scheduled lectures were perfectly tailored to my needs as a physician assistant. The comprehensive coverage of core subjects has significantly improved my diagnostic abilities and patient care.",
              image: require('@/assets/img/testimonials/pa-icon.png')
            }
          ]
        };
        
        commit('setHomepageData', fallbackData);
        commit('setLoading', false);
        commit('setError', { type: 'Homepage Error', message: 'Failed to fetch homepage data, using default values' });
        return fallbackData;
      }
    }
  }
});